import { useRef, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { DropdownMenu } from './DropdownMenu';

export interface TabNavItem {
  id: string;
  label: string;
  icon?: ReactNode;
  /** A small count beside the label (e.g. open tasks). Hidden when null/0. */
  badge?: number | null;
}

export interface TabNavProps {
  items: TabNavItem[];
  activeId: string;
  onSelect: (id: string) => void;
  /** Tabs tucked under a trailing "More" menu so the row does not overflow. */
  overflowItems?: TabNavItem[];
  ariaLabel?: string;
  className?: string;
}

/**
 * The tab row under a page header. Scrolls sideways when it does not fit (the
 * phone layout), supports arrow-key movement between tabs, and can tuck
 * rarely used tabs under "More" — which shows the active tab's name when one
 * of those is open, so the current place is never hidden.
 */
export function TabNav({
  items,
  activeId,
  onSelect,
  overflowItems = [],
  ariaLabel = 'Sections',
  className = '',
}: TabNavProps) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const activeOverflow = overflowItems.find((t) => t.id === activeId);

  const onKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const step = e.key === 'ArrowRight' ? 1 : -1;
    const next = (index + step + items.length) % items.length;
    tabRefs.current[next]?.focus();
    onSelect(items[next].id);
  };

  const tabClass = (active: boolean) =>
    `relative flex items-center gap-1.5 whitespace-nowrap px-1 py-3 text-sm border-b-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 rounded-t ${
      active
        ? 'border-primary-600 text-primary-700 font-semibold'
        : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'
    }`;

  return (
    <nav aria-label={ariaLabel} className={`flex items-end gap-4 ${className}`}>
      <div role="tablist" className="flex items-end gap-4 overflow-x-auto no-scrollbar min-w-0">
        {items.map((tab, i) => {
          const active = tab.id === activeId;
          return (
            <button
              key={tab.id}
              ref={(el) => {
                tabRefs.current[i] = el;
              }}
              type="button"
              role="tab"
              aria-selected={active}
              tabIndex={active || (!items.some((t) => t.id === activeId) && i === 0) ? 0 : -1}
              onClick={() => onSelect(tab.id)}
              onKeyDown={(e) => onKeyDown(e, i)}
              className={tabClass(active)}
            >
              {tab.icon}
              {tab.label}
              {!!tab.badge && (
                <span className="ml-0.5 text-[11px] font-semibold bg-primary-100 text-primary-700 rounded-full px-1.5 py-px">
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {overflowItems.length > 0 && (
        <DropdownMenu
          align="end"
          ariaLabel="More sections"
          items={overflowItems.map((t) => ({
            id: t.id,
            label: t.label,
            icon: t.icon,
            selected: t.id === activeId,
            onSelect: () => onSelect(t.id),
          }))}
          trigger={(props) => (
            <button type="button" {...props} className={tabClass(!!activeOverflow)}>
              {activeOverflow ? activeOverflow.label : 'More'}
              <ChevronDown size={14} />
            </button>
          )}
        />
      )}
    </nav>
  );
}
